import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Collapsible } from '@/components/ui/Collapsible';
import { JsonViewer } from '@/components/ui/JsonViewer';
import { CopyButton } from '@/components/ui/CopyButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { diffJson, summarizeDiff } from '@/lib/jsonDiff';
import { toast } from '@/stores/toast.store';
import {
  fetchMobileApiEndpoints,
  groupEndpoints,
  MobileApiEndpoint,
} from '@/services/mobile-api-docs.api';
import {
  tryLiveCall,
  resetTestData,
  getTestDataStatus,
  TryRole,
  TryEndpointResult,
} from '@/services/mobile-api-try.api';

const METHOD_TONE: Record<string, 'blue' | 'green' | 'amber' | 'red' | 'slate'> = {
  get: 'blue',
  post: 'green',
  put: 'amber',
  patch: 'amber',
  delete: 'red',
};

const ALL_ROLES: TryRole[] = ['admin', 'teacher', 'staff', 'student', 'parent'];

function rolesForPath(path: string): TryRole[] {
  if (path.startsWith('/api/staff/') || path.startsWith('/api/teacher/')) {
    return ['admin', 'teacher', 'staff'];
  }
  if (path.startsWith('/api/student/')) return ['student'];
  if (path.startsWith('/api/parent/')) return ['parent'];
  return ALL_ROLES;
}

function pathParamNames(path: string): string[] {
  const matches = path.match(/\{([^}]+)\}/g) ?? [];
  return matches.map((m) => m.slice(1, -1));
}

export function MobileApiDocsPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['mobile-api-docs'],
    queryFn: fetchMobileApiEndpoints,
  });
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  const groups = data ? groupEndpoints(data) : [];
  const currentGroup = groups.find((g) => g.label === activeGroup) ?? groups[0];

  return (
    <>
      <PageHeader
        title="Mobile API Documentation"
        description="Reference for the frozen SOQ mobile-app contract endpoints — auto-generated from the backend's own OpenAPI decorators, so it never drifts from the real code."
      />

      <TryLiveBanner />

      {isLoading && <p className="text-sm text-slate-500">Loading endpoints…</p>}
      {isError && (
        <p className="text-sm text-red-600">
          Couldn't load /api/docs-json. Is the backend running?
        </p>
      )}

      {data && groups.length > 0 && (
        <div>
          {/* Group switcher — one tab per domain, instead of stacking all
              23 endpoints in one long scroll. */}
          <div className="mb-5 flex flex-wrap gap-1 border-b border-slate-200">
            {groups.map((group) => (
              <TabBtn
                key={group.label}
                active={currentGroup?.label === group.label}
                onClick={() => setActiveGroup(group.label)}
              >
                {group.label}
                <span className="ml-1.5 text-xs font-normal text-slate-400">
                  {group.endpoints.length}
                </span>
              </TabBtn>
            ))}
          </div>

          <div className="space-y-2">
            {currentGroup?.endpoints.map((endpoint) => (
              <EndpointRow key={`${endpoint.method}-${endpoint.path}`} endpoint={endpoint} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function TryLiveBanner() {
  const { data: status, refetch } = useQuery({
    queryKey: ['mobile-api-test-data-status'],
    queryFn: getTestDataStatus,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    setResetting(true);
    try {
      await resetTestData();
      toast.success('Test data reset — synthetic school restored to a clean state.');
      await refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message ?? 'Reset failed');
    } finally {
      setResetting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="text-sm text-amber-900">
        <span className="font-semibold">Try Live</span> calls run only against a dedicated synthetic
        test school (<code className="font-mono text-xs">___api_test_school___</code>) — never real
        schools or real accounts.
        {status?.lastResetAt && (
          <span className="ml-2 text-amber-700">
            Last reset: {new Date(status.lastResetAt).toLocaleString()}
          </span>
        )}
      </div>
      <button
        type="button"
        className="btn-secondary shrink-0"
        onClick={() => setConfirmOpen(true)}
      >
        Reset Test Data
      </button>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleReset}
        title="Reset synthetic test data?"
        message="This wipes every Try Live mutation made so far (created assignments, submitted marks, etc.) and restores the synthetic test school to its known clean starting state. Real schools are never touched."
        confirmText="Reset"
        loading={resetting}
      />
    </div>
  );
}

function EndpointRow({ endpoint }: { endpoint: MobileApiEndpoint }) {
  const fullUrl = `${window.location.origin}${endpoint.path}`;
  const [tryOpen, setTryOpen] = useState(false);

  return (
    <Collapsible
      trigger={
        <div className="max-w-full">
          <div className="flex items-center gap-3">
            <Badge tone={METHOD_TONE[endpoint.method] ?? 'slate'} className="uppercase">
              {endpoint.method}
            </Badge>
            <code className="text-sm font-medium text-slate-800">{endpoint.path}</code>
            <Badge tone={endpoint.requiresAuth ? 'indigo' : 'slate'} className="ml-auto">
              {endpoint.requiresAuth ? 'Auth' : 'Public'}
            </Badge>
          </div>
          {/* On its own line, allowed to wrap — avoids fighting flexbox
              shrink math on a single crowded row (a long summary combined
              with the badges was overflowing the layout's <main> column). */}
          <p className="mt-1 text-sm text-slate-500">{endpoint.summary}</p>
        </div>
      }
    >
      <div className="space-y-4">
        {endpoint.isDesigned && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Response shape designed for this mobile API layer — not matched against a frozen mobile-app
            contract (no saved example exists in the original collection).
          </div>
        )}

        <div>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Full URL
          </span>
          <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <code className="flex-1 truncate text-sm text-slate-700">{fullUrl}</code>
            <CopyButton text={fullUrl} />
          </div>
        </div>

        {endpoint.requestFields.length > 0 && (
          <div>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Request Body
            </span>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Field</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Required</th>
                    <th className="px-3 py-2">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {endpoint.requestFields.map((f) => (
                    <tr key={f.name}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{f.name}</td>
                      <td className="px-3 py-2 text-slate-500">{f.type}</td>
                      <td className="px-3 py-2">
                        {f.required ? <Badge tone="red">required</Badge> : <Badge tone="slate">optional</Badge>}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{f.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {endpoint.successResponse && (
          <JsonViewer data={endpoint.successResponse.example} label={`Response (${endpoint.successResponse.status})`} />
        )}

        {endpoint.errorResponses.length > 0 && (
          <div className="space-y-2">
            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Error responses
            </span>
            {endpoint.errorResponses.map((err) => (
              <div key={err.status}>
                <p className="mb-1 text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">{err.status}</span> — {err.description}
                </p>
                <JsonViewer data={err.example} label={`Example (${err.status})`} />
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-slate-200 pt-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setTryOpen((v) => !v)}
          >
            {tryOpen ? 'Hide Try Live' : 'Try Live'}
          </button>
          {tryOpen && <TryLivePanel endpoint={endpoint} />}
        </div>
      </div>
    </Collapsible>
  );
}

function TryLivePanel({ endpoint }: { endpoint: MobileApiEndpoint }) {
  const roles = rolesForPath(endpoint.path);
  const params = pathParamNames(endpoint.path);

  const [role, setRole] = useState<TryRole>(roles[0]);
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [bodyFields, setBodyFields] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of endpoint.requestFields) {
      if (f.example !== undefined) initial[f.name] = String(f.example);
    }
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TryEndpointResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isBodyMethod = ['post', 'put', 'patch'].includes(endpoint.method);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(bodyFields)) {
        if (v !== '') body[k] = v;
      }
      const res = await tryLiveCall({
        method: endpoint.method,
        path: endpoint.path,
        role,
        pathParams,
        body: isBodyMethod ? body : undefined,
      });
      setResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const exampleData = endpoint.successResponse?.example ?? null;
  const diffNode = result ? diffJson(exampleData, result.body) : null;
  const diffEntries = diffNode ? summarizeDiff(diffNode) : [];

  return (
    <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value as TryRole)}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </Field>

        {params.map((p) => (
          <Field key={p} label={p}>
            <Input
              value={pathParams[p] ?? ''}
              onChange={(e) => setPathParams((prev) => ({ ...prev, [p]: e.target.value }))}
              placeholder={`value for {${p}}`}
            />
          </Field>
        ))}
      </div>

      {isBodyMethod && endpoint.requestFields.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {endpoint.requestFields.map((f) => (
            <Field key={f.name} label={f.name} required={f.required} hint={f.type}>
              <Input
                value={bodyFields[f.name] ?? ''}
                onChange={(e) => setBodyFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
                placeholder={f.description || f.type}
              />
            </Field>
          ))}
        </div>
      )}

      <button type="button" className="btn-primary" onClick={handleSubmit} disabled={loading}>
        {loading ? 'Calling…' : 'Send'}
      </button>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <JsonViewer data={exampleData} label="Saved example" />
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Live response
                </span>
                <Badge tone={result.status < 300 ? 'green' : result.status < 500 ? 'amber' : 'red'}>
                  {result.status}
                </Badge>
              </div>
              <JsonViewer data={result.body} label="" />
            </div>
          </div>

          {diffEntries.length > 0 ? (
            <div>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Differences from saved example ({diffEntries.length})
              </span>
              <ul className="space-y-1 rounded-md border border-slate-200 bg-white p-2 text-xs">
                {diffEntries.slice(0, 30).map((d) => (
                  <li key={d.path} className="flex items-start gap-2">
                    <Badge
                      tone={d.status === 'added' ? 'green' : d.status === 'removed' ? 'red' : 'amber'}
                      className="shrink-0"
                    >
                      {d.status}
                    </Badge>
                    <code className="font-mono text-slate-700">{d.path}</code>
                  </li>
                ))}
              </ul>
            </div>
          ) : exampleData !== null ? (
            <p className="text-xs text-green-700">Live response matches the saved example exactly.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'border-b-2 px-4 py-2 text-sm font-medium transition',
        active ? 'border-brand-500 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
